import { useState } from "react";
import { Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { categoriesFor, SOURCES, MODES } from "@/lib/prospectMeta";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";
const labelCls = "text-xs font-semibold text-slate-500 mb-1 block";

function F({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

const empty = {
  prospect_type: "individu", category: "", full_name: "", guardian_name: "", institution_name: "",
  contact_person: "", position: "", phone: "", email: "", school_or_company: "", class_or_job: "",
  address: "", program_interest: "", learning_goal: "", mode: "", desired_schedule: "",
  implementation_plan: "", participant_count: "", negotiation_notes: "", source: "", assigned_to: "", notes: "",
};

export function ProspectForm({ mode, initial, members, onClose, onSaved }) {
  const [f, setF] = useState({ ...empty, ...(initial || {}), phone: initial?.phone_raw || initial?.phone || "" });
  const [error, setError] = useState("");
  const [dups, setDups] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const isLembaga = f.prospect_type === "lembaga";

  const save = async (force = false) => {
    setError(""); setLoading(true);
    try {
      const url = mode === "edit" ? `/prospects/${initial.id}?force=${force}` : `/prospects?force=${force}`;
      const method = mode === "edit" ? "put" : "post";
      const { data } = await api[method](url, f);
      toast.success(mode === "edit" ? "Prospek diperbarui." : "Prospek ditambahkan.");
      onSaved(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicates) {
        setDups(detail.duplicates);
        setError(detail.message || "Data duplikat terdeteksi.");
      } else {
        setError(formatApiErrorDetail(detail) || e.message);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="prospect-form">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-lg font-semibold text-slate-900">{mode === "edit" ? "Edit Prospek" : "Tambah Prospek"}</h3>
          <button onClick={onClose} data-testid="prospect-form-close" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="p-6 space-y-4">
          <div className="flex gap-2">
            {["individu", "lembaga"].map((t) => (
              <button type="button" key={t} data-testid={`prospect-type-${t}`} onClick={() => setF((p) => ({ ...p, prospect_type: t, category: "" }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border ${f.prospect_type === t ? "bg-[#0f2847] text-white border-[#0f2847]" : "bg-white text-slate-600 border-slate-300"}`}>
                {t === "individu" ? "Individu" : "Lembaga / Corporate"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Kategori">
              <select className={inputCls} value={f.category} onChange={set("category")} data-testid="prospect-category" required>
                <option value="">— Pilih —</option>
                {categoriesFor(f.prospect_type).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </F>
            {!isLembaga ? (
              <F label="Nama Lengkap"><input className={inputCls} value={f.full_name} onChange={set("full_name")} data-testid="prospect-full-name" required /></F>
            ) : (
              <F label="Nama Lembaga / Perusahaan"><input className={inputCls} value={f.institution_name} onChange={set("institution_name")} data-testid="prospect-institution-name" required /></F>
            )}

            {!isLembaga && <F label="Nama Orang Tua / Wali"><input className={inputCls} value={f.guardian_name} onChange={set("guardian_name")} /></F>}
            {isLembaga && <F label="Nama Kontak / PIC"><input className={inputCls} value={f.contact_person} onChange={set("contact_person")} data-testid="prospect-contact-person" /></F>}
            {isLembaga && <F label="Jabatan"><input className={inputCls} value={f.position} onChange={set("position")} /></F>}

            <F label="Nomor WhatsApp"><input className={inputCls} value={f.phone} onChange={set("phone")} placeholder="0812xxxx / 62812xxxx" data-testid="prospect-phone" required /></F>
            <F label="Email"><input type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="prospect-email" /></F>

            {!isLembaga && <F label="Sekolah / Universitas / Perusahaan"><input className={inputCls} value={f.school_or_company} onChange={set("school_or_company")} /></F>}
            {!isLembaga && <F label="Kelas / Pekerjaan"><input className={inputCls} value={f.class_or_job} onChange={set("class_or_job")} /></F>}

            <F label={isLembaga ? "Alamat" : "Kecamatan / Alamat"}><input className={inputCls} value={f.address} onChange={set("address")} /></F>
            <F label={isLembaga ? "Program yang Dibutuhkan" : "Program yang Diminati"}><input className={inputCls} value={f.program_interest} onChange={set("program_interest")} data-testid="prospect-program" /></F>

            {!isLembaga && <F label="Tujuan Belajar"><input className={inputCls} value={f.learning_goal} onChange={set("learning_goal")} /></F>}
            {isLembaga && <F label="Jumlah Calon Peserta"><input className={inputCls} value={f.participant_count} onChange={set("participant_count")} /></F>}

            <F label="Mode Belajar">
              <select className={inputCls} value={f.mode} onChange={set("mode")}>
                <option value="">— Pilih —</option>{MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </F>
            <F label={isLembaga ? "Rencana Pelaksanaan" : "Jadwal yang Diinginkan"}><input className={inputCls} value={isLembaga ? f.implementation_plan : f.desired_schedule} onChange={set(isLembaga ? "implementation_plan" : "desired_schedule")} /></F>

            <F label="Sumber Prospek">
              <select className={inputCls} value={f.source} onChange={set("source")} data-testid="prospect-source">
                <option value="">— Pilih —</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </F>
            <F label="PIC / Admin">
              <select className={inputCls} value={f.assigned_to} onChange={set("assigned_to")} data-testid="prospect-pic">
                <option value="">— Pilih —</option>{(members || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </F>
          </div>

          <F label={isLembaga ? "Catatan Kebutuhan / Negosiasi" : "Catatan"}>
            <textarea className={inputCls} rows={2} value={isLembaga ? f.negotiation_notes : f.notes} onChange={set(isLembaga ? "negotiation_notes" : "notes")} />
          </F>

          {error && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="prospect-form-error">
              <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {error}</div>
              {dups.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {dups.map((d) => <li key={d.id}>{d.institution_name || d.full_name} · {d.phone_normalized} {d.email ? `· ${d.email}` : ""}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-lg">Batal</button>
            {dups.length > 0 && (
              <button type="button" onClick={() => save(true)} disabled={loading} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-medium px-4 py-2.5 rounded-lg" data-testid="prospect-save-force">Simpan Tetap</button>
            )}
            <button type="submit" disabled={loading} className="flex-1 bg-[#0f2847] hover:bg-[#17416f] text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="prospect-save-button">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
